import React, { useState } from "react";
import { FishboneAnalysis } from "./types";

interface FishboneAnalysisComponentProps {
  fishboneAnalysis: FishboneAnalysis;
  rootCauseDescription: string;
  onChange: (updated: FishboneAnalysis) => void;
  readOnly?: boolean;
}

// ─── Category configuration ──────────────────────────────────────────────────
const CATEGORIES = [
  { key: "man" as const, label: "Man", color: "#dc2626", position: "top" as const, spineX: 280 },
  { key: "machine" as const, label: "Machine", color: "#16a34a", position: "top" as const, spineX: 680 },
  { key: "management" as const, label: "Management", color: "#7c3aed", position: "top" as const, spineX: 1080 },
  { key: "material" as const, label: "Material", color: "#2563eb", position: "bottom" as const, spineX: 480 },
  { key: "system" as const, label: "System", color: "#ea580c", position: "bottom" as const, spineX: 880 },
];

const DESCRIPTIONS: Record<string, string> = {
  man: "Human factors: lack of training, communication, awareness, lack of adequate human power, arson etc.",
  material:
    "Lack of materials, inventories, safety devices/equipment needed to mitigate/prevent the risk of fire & Environmental factor/poor storage practices.",
  machine: "Faulty machinery or unavailability of redundant machineries.",
  system:
    "Lack of periodical inspection, non-compliance to standards & no preventive maintenance activity.",
  management: "Expenditure & Budget problems.",
};

// ─── SVG geometry constants ──────────────────────────────────────────────────
const SPINE_Y = 400;
const TOP_TIP_Y = 30;
const BOTTOM_TIP_Y = 770;
const BRANCH_DX = -220; // horizontal offset for branch tip
const RIB_LEN = 140; // length of each factor rib line
const MAX_VISIBLE = 4; // max factors visible on diagram per branch
const BUBBLE_W = 210; // width of factor text bubble
const BUBBLE_H = 75; // max height of factor text bubble

// ─── Component ───────────────────────────────────────────────────────────────
const FishboneAnalysisComponent: React.FC<FishboneAnalysisComponentProps> = ({
  fishboneAnalysis,
  rootCauseDescription,
  onChange,
  readOnly = false,
}) => {
  const [activeCategory, setActiveCategory] = useState<keyof FishboneAnalysis | null>(null);

  const MAX_RC_LEN = 40;
  const rootCauseLabel =
    (rootCauseDescription || "").length > MAX_RC_LEN
      ? rootCauseDescription.slice(0, MAX_RC_LEN) + "..."
      : rootCauseDescription || "No root cause";


  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleAddFactor = (category: keyof FishboneAnalysis) => {
    const updated = { ...fishboneAnalysis };
    updated[category] = [...updated[category], ""];
    onChange(updated);
  };

  const handleUpdateFactor = (
    category: keyof FishboneAnalysis,
    index: number,
    value: string
  ) => {
    const updated = { ...fishboneAnalysis };
    updated[category] = [...updated[category]];
    updated[category][index] = value;
    onChange(updated);
  };

  const handleRemoveFactor = (
    category: keyof FishboneAnalysis,
    index: number
  ) => {
    const updated = { ...fishboneAnalysis };
    updated[category] = updated[category].filter((_, i) => i !== index);
    onChange(updated);
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  const toggleCategory = (key: keyof FishboneAnalysis) =>
    setActiveCategory((prev) => (prev === key ? null : key));

  // ── Render a single branch with ribs ─────────────────────────────────────
  const renderBranch = (cat: (typeof CATEGORIES)[number]) => {
    const factors = fishboneAnalysis[cat.key];
    const isTop = cat.position === "top";
    const tipY = isTop ? TOP_TIP_Y : BOTTOM_TIP_Y;
    const tipX = cat.spineX + BRANCH_DX;
    const isActive = activeCategory === cat.key;

    const start = { x: cat.spineX, y: SPINE_Y };
    const end = { x: tipX, y: tipY };

    const visibleFactors = factors.slice(0, MAX_VISIBLE);
    const extraCount = factors.length - MAX_VISIBLE;

    return (
      <g
        key={cat.key}
        className="cursor-pointer"
        onClick={() => toggleCategory(cat.key)}
      >
        {/* Main diagonal branch line */}
        <line
          x1={start.x}
          y1={start.y}
          x2={end.x}
          y2={end.y}
          stroke={cat.color}
          strokeWidth={isActive ? 3.5 : 2.5}
          strokeLinecap="round"
          opacity={isActive ? 1 : 0.8}
        />

        {/* Dot at spine connection */}
        <circle cx={start.x} cy={start.y} r={4.5} fill={cat.color} />

        {/* ── Category label pill ── */}
        <rect
          x={end.x - 75}
          y={isTop ? end.y - 36 : end.y + 12}
          width={150}
          height={34}
          rx={17}
          fill={cat.color}
          opacity={isActive ? 1 : 0.9}
          className="transition-opacity hover:opacity-100"
        />
        <text
          x={end.x}
          y={isTop ? end.y - 12 : end.y + 35}
          textAnchor="middle"
          fontSize="16"
          fontWeight="bold"
          fill="white"
          style={{ pointerEvents: "none", letterSpacing: "0.5px" }}
        >
          {cat.label}
        </text>

        {/* Factor count badge (if any) */}
        {factors.length > 0 && (
          <>
            <circle
              cx={end.x + 70}
              cy={isTop ? end.y - 17 : end.y + 21}
              r={12}
              fill="white"
              stroke={cat.color}
              strokeWidth={2}
            />
            <text
              x={end.x + 70}
              y={isTop ? end.y - 13 : end.y + 25}
              textAnchor="middle"
              fontSize="10"
              fontWeight="700"
              fill={cat.color}
              style={{ pointerEvents: "none" }}
            >
              {factors.length}
            </text>
          </>
        )}

        {/* ── Factor ribs along the branch ── */}
        {visibleFactors.map((factor, i) => {
          const n = visibleFactors.length;
          const t = (i + 1) / (n + 1);

          // Point on the diagonal branch
          const px = start.x + t * (end.x - start.x);
          const py = start.y + t * (end.y - start.y);

          // Rib goes horizontally to the left
          const ribEndX = px - RIB_LEN;

          return (
            <g key={i}>
              {/* Rib line */}
              <line
                x1={px}
                y1={py}
                x2={ribEndX}
                y2={py}
                stroke={cat.color}
                strokeWidth={1.2}
                opacity={0.55}
              />
              {/* Connection dot */}
              <circle cx={px} cy={py} r={2.5} fill={cat.color} opacity={0.7} />

              <foreignObject
                x={ribEndX - BUBBLE_W - 4}
                y={py - 14}
                width={BUBBLE_W}
                height={BUBBLE_H}
              >
                <div
                  style={{
                    fontSize: "14px",
                    lineHeight: "18px",
                    padding: "6px 12px",
                    background: "#ffffff",
                    border: `1px solid ${cat.color}40`,
                    borderLeft: `5px solid ${cat.color}`,
                    borderRadius: "6px",
                    color: factor ? "#0f172a" : "#94a3b8",
                    fontStyle: factor ? "normal" : "italic",
                    fontWeight: factor ? "500" : "400",
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical" as React.CSSProperties["WebkitBoxOrient"],
                    boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                    wordBreak: "break-word",
                    maxHeight: `${BUBBLE_H - 2}px`,
                  }}
                >
                  {factor || "(empty)"}
                </div>
              </foreignObject>
            </g>
          );
        })}

        {/* "+N more" indicator */}
        {extraCount > 0 && (
          <text
            x={end.x}
            y={isTop ? end.y + 18 : end.y - 10}
            textAnchor="middle"
            fontSize="10"
            fill={cat.color}
            fontStyle="italic"
            opacity={0.8}
          >
            +{extraCount} more
          </text>
        )}
      </g>
    );
  };

  // ── Main render ──────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      <div className="bg-blue-50 border border-blue-100 p-4 rounded-lg">
        <h4 className="font-semibold text-blue-900 mb-1">
          Fishbone Analysis Diagram
        </h4>
        <p className="text-sm text-blue-800">
          Fishbone diagram analysis consists of 6 categories that could have led to the contributing factors of adverse event
        </p>
      </div>

      {/* ──────── SVG Fishbone Diagram ──────── */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl shadow-inner p-4 w-full overflow-hidden">
        <svg
          viewBox="-220 0 1850 800"
          className="w-full h-auto drop-shadow-sm"
          style={{ minWidth: 800, minHeight: 650 }}
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Arrow marker */}
          <defs>
            <marker
              id="spine-arrow"
              markerWidth="12"
              markerHeight="8"
              refX="11"
              refY="4"
              orient="auto"
            >
              <polygon points="0 0, 12 4, 0 8" fill="#374151" />
            </marker>
          </defs>

          {/* ── Spine (horizontal main line) ── */}
          <line
            x1={50}
            y1={SPINE_Y}
            x2={1320}
            y2={SPINE_Y}
            stroke="#1e293b"
            strokeWidth={5}
            strokeLinecap="round"
            markerEnd="url(#spine-arrow)"
          />

          {/* ── Effect box (fish head) ── */}
          <rect
            x={1335}
            y={SPINE_Y - 60}
            width={280}
            height={120}
            rx={16}
            fill="#0f766e"
            stroke="#042f2e"
            strokeWidth={3}
          />
          <text
            x={1335 + 140}
            y={SPINE_Y - 25}
            textAnchor="middle"
            fontSize="20"
            fill="#ffffff"
            fontWeight="bold"
            letterSpacing="1.5"
          >
            ROOT CAUSE
          </text>

          <foreignObject
            x={1335 + 15}            // small left padding
            y={SPINE_Y - 5}          // below the label
            width={280 - 30}         // width - left/right padding
            height={120 - 45}        // height - top/label space
          >
            <div
              style={{
                fontSize: "15px",
                lineHeight: "20px",
                color: "#ccfbf1",
                fontWeight: "600",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                wordBreak: "break-word",
                textAlign: "center"
              }}
            >
              {rootCauseDescription || "No root cause"}
            </div>
          </foreignObject>
          {/* <text
            x={1130}
            y={SPINE_Y + 12}
            textAnchor="middle"
            fontSize="11"
            fill="white"
            fontWeight="700"
          >
            {rootCauseLabel || "No root Cause"}
          </text> */}

          {/* ── Render all category branches ── */}
          {CATEGORIES.map((cat) => renderBranch(cat))}
        </svg>
      </div>

      <div className="space-y-6">
        {CATEGORIES.map((cat) => (
          <div key={cat.key} className="border rounded-lg p-4">
            <div className="mb-3">
              <h5 className="font-semibold text-gray-800">{cat.label}</h5>
              <p className="text-sm text-gray-600">{DESCRIPTIONS[cat.key]}</p>
            </div>

            <div className="space-y-2">
              {fishboneAnalysis[cat.key].length === 0 ? (
                <p className="text-gray-500 text-sm italic">No factors added</p>
              ) : (
                fishboneAnalysis[cat.key].map((factor, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    {readOnly ? (
                      <div className="flex-1 p-2 border rounded bg-gray-50 text-gray-800 break-words whitespace-pre-wrap">
                        {factor || <span className="text-gray-400 italic">No factor provided</span>}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={factor}
                        onChange={(e) =>
                          handleUpdateFactor(cat.key, index, e.target.value)
                        }
                        className="flex-1 p-2 border rounded"
                        placeholder={`Enter ${cat.label.toLowerCase()} factor`}
                      />
                    )}
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => handleRemoveFactor(cat.key, index)}
                        className="px-3 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>

            {!readOnly && (
              <button
                type="button"
                onClick={() => handleAddFactor(cat.key)}
                className="mt-3 px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-500 text-sm"
              >
                Add {cat.label} Factor
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FishboneAnalysisComponent;
